import { useState } from 'react';
import { z } from 'zod';
import {
    CommanderFormProps,
    DateField,
    SchemaFormDefinition,
    SchemaFormFields,
    useHandledDialogFormControls,
} from '@mews-commander/form';
import {
    BusinessError,
    createExperimentalZodFormResolver,
    getBase64,
    getDefaults,
    lt,
    Validation,
} from '@mews/framework';
import {
    ReservationUploadedContractFileSignatureStatus,
    ReservationUploadedContractFileUpdateParameters,
    useRepositorySuspenseQuery,
} from '@mews-commander/data-access';
import { useCalendarService } from '@mews-commander/ui';
import { StructuredDate } from '@optimus-web/core';
import { ChangeFileField, ChangeFileFieldProps } from '../../common/components/ChangeFileField';
import { SIGNATURE_STATUS_OPTIONS, FILE_SCHEMA_OPTIONS } from '../../common/constants';
import { useUpdateContractMutation } from './hooks/useUpdateContractMutation';
import { useDocumentsTableContext } from './DocumentsTableContext';
import { useOpenContractInNewTab } from './hooks/useOpenContractInNewTab';

const editFileSchema = (context: { isNewFile: boolean }) =>
    z.object({
        name: z.string().min(1).max(255),
        file: context.isNewFile
            ? Validation.File.Default(FILE_SCHEMA_OPTIONS)
            : Validation.File.Default(FILE_SCHEMA_OPTIONS).optional(),
        signatureStatus: z
            .nativeEnum(ReservationUploadedContractFileSignatureStatus)
            .or(z.literal(''))
            .optional()
            .transform((value) => (value === '' ? undefined : value)),
        signatureDate: Validation.Date.Iso.optional(),
        signatureDeadlineDate: Validation.Date.Iso.optional(),
        expirationDate: Validation.Date.Iso.optional(),
        notes: z.string().max(1000).optional(),
    });

export type EditFileDialogFormValues = z.infer<ReturnType<typeof editFileSchema>>;

interface EditFileDialogFormDefinitionProps extends Omit<ChangeFileFieldProps, 'fieldProps'> {
    signatureStatus: ReservationUploadedContractFileSignatureStatus | undefined;
    todayDate: StructuredDate;
}

const editFileDialogFormDefinition = (
    props: EditFileDialogFormDefinitionProps
): SchemaFormDefinition<ReturnType<typeof editFileSchema>> => ({
    fields: {
        name: {
            label: lt('Name'),
        },
        file: {
            label: lt('File'),
            render: (fieldProps) => <ChangeFileField {...props} fieldProps={fieldProps} />,
        },
        signatureStatus: {
            label: lt('ContractsTableSignatureStatusLabel'),
            field: {
                type: 'select',
                options: SIGNATURE_STATUS_OPTIONS,
                disableInputSearch: true,
            },
        },
        signatureDate: {
            label: lt('ContractsTableSignatureDateLabel'),
            render: (fieldProps) =>
                props.signatureStatus === ReservationUploadedContractFileSignatureStatus.Signed ? (
                    <DateField mode="iso" maxDate={props.todayDate} {...fieldProps} />
                ) : null,
        },
        signatureDeadlineDate: {
            label: lt('ContractSignatureDeadlineDateLabel'),
            render: (fieldProps) =>
                props.signatureStatus === ReservationUploadedContractFileSignatureStatus.Pending ? (
                    <DateField
                        mode="iso"
                        minDate={props.todayDate}
                        helper={lt('ContractSignatureDeadlineDateHelperText')}
                        {...fieldProps}
                    />
                ) : null,
        },
        expirationDate: {
            label: lt('ExpirationDate'),
            field: { type: 'date', mode: 'iso' },
        },
        notes: {
            label: lt('Note'),
            field: {
                type: 'textarea',
                rows: 1,
            },
        },
    },
});

interface EditFileDialogFormProps extends CommanderFormProps {
    documentId: string;
}

export const EditFileDialogForm = (props: EditFileDialogFormProps) => {
    const [isNewFile, setIsNewFile] = useState(false);
    const { reservationId } = useDocumentsTableContext();
    const { mutate: openContract, isPending } = useOpenContractInNewTab();
    const { mutateAsync: updateContractMutation } = useUpdateContractMutation();
    const { data: contractData } = useRepositorySuspenseQuery('reservationUploadedContractFiles', 'getAll', {
        ids: [props.documentId],
    });
    const calendarService = useCalendarService();
    const today = calendarService.getCurrentDate();

    const contract = contractData.uploadedContractFiles?.[0];

    if (contract == null) {
        throw new BusinessError('Contract not found');
    }

    const { renderFormContent, watch } = useHandledDialogFormControls<EditFileDialogFormValues>({
        ...props,
        defaultValues: getDefaults(editFileSchema({ isNewFile })),
        resolver: createExperimentalZodFormResolver(editFileSchema({ isNewFile })),
        onSubmit: async (values) => {
            const fileItem = values.file?.[0];
            const base64Data = fileItem ? await getBase64(fileItem) : undefined;
            const parameters: ReservationUploadedContractFileUpdateParameters = {
                reservationId,
                id: contract.id,
                name: values.name,
                signatureStatus: values.signatureStatus,
                signatureDate:
                    values.signatureStatus === ReservationUploadedContractFileSignatureStatus.Signed
                        ? values.signatureDate
                        : undefined,
                signatureDeadlineDate:
                    values.signatureStatus === ReservationUploadedContractFileSignatureStatus.Pending
                        ? values.signatureDeadlineDate
                        : undefined,
                expirationDate: values.expirationDate,
                notes: values.notes,
                file:
                    fileItem != null && base64Data != null
                        ? {
                              value: {
                                  name: fileItem.name,
                                  mimeType: fileItem.type,
                                  data: base64Data,
                              },
                          }
                        : undefined,
            };
            await updateContractMutation(parameters);
        },
        values: {
            name: contract.name,
            signatureStatus: contract.signatureStatus ?? undefined,
            signatureDate: contract.signatureDate ?? undefined,
            expirationDate: contract.expirationDate ?? undefined,
            notes: contract.notes ?? undefined,
        },
    });

    const signatureStatus = watch('signatureStatus');

    return renderFormContent(
        <SchemaFormFields
            schema={editFileSchema({ isNewFile })}
            formDefinition={editFileDialogFormDefinition({
                fileName: contract.fileName,
                fileId: contract.id,
                isNewFile,
                setIsNewFile,
                signatureStatus,
                openFile: openContract,
                isPending,
                todayDate: today,
            })}
        />
    );
};
